import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  DetailRow,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatHours(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

function formatDateTime(value: string | null) {
  if (!value) return "Not set";
  return new Date(value).toLocaleString();
}

export default function TimeEntryDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const timeEntryId = normalizeParam(params.id);
  const workspace = usePrimaryWorkspace();
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);

  const entry =
    (timeEntries.data ?? []).find((record) => record.id === timeEntryId) ??
    null;
  const project = entry?.project_id
    ? (projects.data ?? []).find((record) => record.id === entry.project_id)
    : null;
  const clientId = entry?.client_id ?? project?.client_id ?? null;
  const client = clientId
    ? (clients.data ?? []).find((record) => record.id === clientId)
    : null;
  const task = entry?.task_id
    ? (tasks.data ?? []).find((record) => record.id === entry.task_id)
    : null;
  const invoice = entry?.invoice_id
    ? (invoices.data ?? []).find((record) => record.id === entry.invoice_id)
    : null;
  const valueCents = entry ? billableValue(entry) : 0;
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    tasks.error ??
    timeEntries.error ??
    invoices.error;
  const errorMessage = error instanceof Error ? error.message : "";

  if (workspace.isLoading || timeEntries.isLoading) {
    return (
      <ProtectedScreen title="Time Entry" subtitle="Loading time details.">
        <Card gap="sm">
          <AppText>Loading...</AppText>
        </Card>
      </ProtectedScreen>
    );
  }

  if (!entry) {
    return (
      <ProtectedScreen title="Time entry not found">
        <Card gap="md">
          <AppText size="sm">
            This time entry could not be found in the current workspace.
          </AppText>
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push("/time-entries" as never)}
          >
            <ButtonText>Back to time entries</ButtonText>
          </Button>
        </Card>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      title={entry.description ?? "Time entry"}
      subtitle="Review one tracked work record, its billing value, and where it sits in the client/project/task/invoice workflow."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText tone="danger" weight="semibold">
            Time entry details could not load
          </AppText>
          <AppText tone="danger" size="xs">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <View style={stylesheet.summaryGrid}>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Duration</AppText>
          <AppText size="lg" weight="bold">
            {formatHours(entry.duration_seconds)}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Billing</AppText>
          <AppText size="lg" weight="bold">
            {entry.billable ? "Billable" : "Non-billable"}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Value</AppText>
          <AppText size="lg" weight="bold">
            {entry.billable ? formatMoney(valueCents) : "$0.00"}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Invoice state</AppText>
          <AppText size="lg" weight="bold">
            {entry.invoiced_at ? "Invoiced" : "Unbilled"}
          </AppText>
        </Card>
      </View>

      <View style={stylesheet.detailGrid}>
        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Tracked work"
            description="Source, timing, duration, and rate for this entry."
          />
          <Stack gap="sm">
            <DetailRow label="Source" value={entry.source} />
            <DetailRow
              label="Started"
              value={formatDateTime(entry.started_at)}
            />
            <DetailRow label="Ended" value={formatDateTime(entry.ended_at)} />
            <DetailRow
              label="Hourly rate"
              value={
                entry.hourly_rate_cents
                  ? `${formatMoney(entry.hourly_rate_cents)}/h`
                  : "Not set"
              }
            />
          </Stack>
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Billing position"
            description="Whether this work can still be edited or billed."
          />
          <Stack gap="sm">
            <DetailRow
              label="Status"
              value={entry.invoiced_at ? "Locked to invoice" : "Editable"}
            />
            <DetailRow
              label="Invoiced at"
              value={formatDateTime(entry.invoiced_at)}
            />
            <DetailRow
              label="Calculated value"
              value={entry.billable ? formatMoney(valueCents) : "Non-billable"}
            />
            <DetailRow
              label="Invoice"
              value={invoice?.invoice_number ?? "Not invoiced"}
            />
          </Stack>
        </Card>
      </View>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Workflow links</AppText>
          <AppText size="xs">
            Jump to the records this time entry supports.
          </AppText>
        </Stack>
        <View style={stylesheet.linkGrid}>
          <View style={stylesheet.linkBlock}>
            <AppText size="xs">Client</AppText>
            <AppText weight="semibold">{client?.name ?? "No client"}</AppText>
            {client ? (
              <Button
                compact
                variant="outline"
                onPress={() => router.push(`/clients/${client.id}` as never)}
              >
                <ButtonText>View client</ButtonText>
              </Button>
            ) : null}
          </View>
          <View style={stylesheet.linkBlock}>
            <AppText size="xs">Project</AppText>
            <AppText weight="semibold">{project?.name ?? "No project"}</AppText>
            {project ? (
              <Button
                compact
                variant="outline"
                onPress={() => router.push(`/projects/${project.id}` as never)}
              >
                <ButtonText>View project</ButtonText>
              </Button>
            ) : null}
          </View>
          <View style={stylesheet.linkBlock}>
            <AppText size="xs">Task</AppText>
            <AppText weight="semibold">{task?.name ?? "No task"}</AppText>
            {task ? (
              <Button
                compact
                variant="outline"
                onPress={() => router.push(`/tasks/${task.id}` as never)}
              >
                <ButtonText>View task</ButtonText>
              </Button>
            ) : null}
          </View>
          <View style={stylesheet.linkBlock}>
            <AppText size="xs">Invoice</AppText>
            <AppText weight="semibold">
              {invoice?.invoice_number ?? "Not invoiced"}
            </AppText>
            {invoice ? (
              <Button
                compact
                variant="outline"
                onPress={() => router.push(`/invoices/${invoice.id}` as never)}
              >
                <ButtonText>View invoice</ButtonText>
              </Button>
            ) : null}
          </View>
        </View>
      </Card>

      <View style={stylesheet.actions}>
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/time-entries" as never)}
        >
          <ButtonText>Back to time entries</ButtonText>
        </Button>
      </View>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    summaryCard: { flexGrow: 1, flexShrink: 1, flexBasis: 150, minWidth: 140 },
    detailCard: { flexGrow: 1, flexShrink: 1, flexBasis: 320, minWidth: 260 },
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    linkGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    linkBlock: {
      minWidth: 180,
      flex: 1,
      gap: spacing.s,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
